import { Form } from 'antd';
import { delay, get, set } from 'lodash';
import { ISearchCustomerItem } from 'modules/customer/widgets/AddCustomer/type';
import homeApis from 'modules/dashboard/services/apis';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import {
  IApiUpdateTicketBody
} from 'modules/POS/services/types/ticket';
import { IStaffItem } from 'modules/shop/types/staff';
import { verifyPasscode, verifyPasscodeWithConfig } from 'modules/shop/widgets/ModalCheckPassCode';
import moment from 'moment';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { IResponseData } from 'services/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatPhone } from 'utils/formatPhone';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import toast from 'utils/toast';
import uuid from 'utils/uuid';
import { setPageLoading } from 'widgets/PageLoading';
import { CASHIER_ITEM_TYPE } from './cashier.constants';
import { cashierCtrl, cashierRef, cashierScannerCtrl, globalCashierDOMRef } from './cashier.controller';
import {
  ICashierControllerRef,
  IUICashierFormValues,
  IUISelectItem,
  UIPaymentDetailsData
} from './cashier.interface';
import { calcDiscountForItem, generateKeyInitialValue, mappingSavePayload } from './cashier.utils';
import ModalEditPriceItem, { globalModalEditPriceItemRef } from './features/AddPrice/EditPrice';
import DiscountByItem, {
  globalDiscountByItemRef,
} from './features/Discount/DiscountModalV2/DiscountByItem';
import { DISCOUNT_FORM_TYPE } from './features/Discount/DiscountModalV2/SalonDiscountTab';
import ModalAlertEmpty, { useModalAlertEmptyRef } from './features/ModalAlertEmpty';
import ModalUpdateQuantity, {
  globalModalUpdateQuantityRef,
} from './features/UpdateQuantity';
import FooterActions from './FooterActions';
import useAutoSaveGuard from './hooks/useAutoSaveGuard';
import useCalculateCashier from './hooks/useCalculateCashier';
import { useSocketContext } from './hooks/useSocket';
import CashierProducts from './Products';
import { TypePaymentActors, TypePaymentPassData } from './socket.interface';
import CashierTicketInfo from './TicketInfo';
import { bilSelectionRef } from './BillSelections';
import { IssueFormValues } from './features/SellGift/ModalSelfGift/IssueGiftCard';
import { IProductVIPItemData } from 'modules/shop/types/vip';

const CashierPageUI = forwardRef<ICashierControllerRef>(({ }, ref) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [autoSaveControl, modalAutoSaveContext] = useAutoSaveGuard(form);
  const { calculateCashier, saveSettingConfigs } = useCalculateCashier(form);
  const activeStaffCashier = cashierService.selectors.activeStaffCashier();
  const alertEmptyRef = useModalAlertEmptyRef();
  const socket = useSocketContext();

  const handleSaveTicket = (values: IUICashierFormValues) => {
    return checkStaffExist(async (staffId) => {
      const body: IApiUpdateTicketBody = mappingSavePayload({ ...values, staffId });
      if (!body.items.length || (body.items.length === 1 && !body.items[0].quantity)) {
        return alertEmptyRef.current?.open();
      }
      setPageLoading(true);
      try {
        const res: IResponseData<string> = await cashierApis.updateTicket(body);
        if (res.data.data) {
          form.setFieldValue('billId', res.data.data);
          dispatch(cashierService.actions.async.lstBillOnHold.fetch());
          toast.success('Saved!');
          if (!autoSaveControl.isDone()) { }
          autoSaveControl.reset();
        }
      } catch (error) {
      } finally {
        setPageLoading(false);
      }
    });
  };

  const generateNewTicket = async (staffId?: string) => {
    const _staffId = activeStaffCashier?.id || staffId;
    if (_staffId) form.setFieldValue('staffId', _staffId);
    try {
      const res: IResponseData<number> =
        await cashierApis.getNumberTicket();
      if (res.data.data) {
        form.resetFields();
        socket.backToCheckIn();
        form.setFieldsValue({
          ...saveSettingConfigs(),
          staffId: _staffId,
          ticketNumber: res.data.data,
        } as IUICashierFormValues);
      }
    } catch (error) {
    } finally {
    }
  };
  const generateNewTicketAfterPayment = async (staffId?: string) => {
    const _staffId = activeStaffCashier?.id || staffId;
    if (_staffId) form.setFieldValue('staffId', _staffId);
    try {
      const res: IResponseData<number> =
        await cashierApis.getNumberTicket();
      if (res.data.data) {
        form.resetFields();
        form.setFieldsValue({
          ...saveSettingConfigs(),
          staffId: _staffId,
          ticketNumber: res.data.data,
        } as IUICashierFormValues);
      }
    } catch (error) {
    } finally {
    }
  };

  const requestStaffForNewTicket = () => {
    const staffId = form.getFieldValue('staffId');
    if (staffId) return generateNewTicket(staffId);
    if (activeStaffCashier?.id) {
      return generateNewTicket(activeStaffCashier?.id);
    }
    verifyPasscode(async (staff) => {
      dispatch(cashierService.actions.saveStaffCashier(staff));
      await generateNewTicket(staff.id);
    });
  };

  const checkStaffExist = <ReturnType extends any>(callback: (staffId: string) => ReturnType) => {
    const staffId = form.getFieldValue('staffId');
    if (staffId) return callback(staffId);
    return verifyPasscodeWithConfig(async (staff) => {
      try {
        const res = await homeApis.staff.checkIn({ passcode: staff.passCode }, staff.id);
        if (res.data.data) {
          form.setFieldValue('staffId', staff.id);
          dispatch(cashierService.actions.saveStaffCashier(staff));
          return callback(staff.id);
        } else {
          toast.error(res.data?.message || 'Failed to clock in');
          return null;
        }
      } catch (error) { }
    }, {
      title: 'Clock In'
    });
  };

  const switchTicket: ICashierControllerRef['switchTicket'] = async (
    billId: string
  ) => {
    setPageLoading(true);
    try {
      const res: IResponseData<IBillInfoResData> =
        await cashierApis.getBillById(billId);
      if (res.data.data) {
        const info = res.data.data;
        const formValue: IUICashierFormValues = form.getFieldsValue();
        const values: IUICashierFormValues = {
          ...formValue,
          ...saveSettingConfigs(),
          billId: info.billId,
          balance: 0,
          customerId: info.customerId || '',
          customerName: info.customerName || '',
          customerPhone: info.customerPhone || '',
          customerPoint: info.customerPoint || 0,
          ticketNumber: info.ticketNumber,
          createdDate: info.createdDated,
          total: info.total,
          subTotal: info.subTotal,
          discount: info.discount,
          saleTax: info.saleTax,
          useTax: info.useTax,
          // staffId: info.staffId || '',
          items: info.items.map((o) => ({
            customerId: info.customerId || '',
            discount: o.discount,
            itemId: o.itemId,
            itemName: o.itemName,
            itemType: o.itemType,
            price: o.price,
            quantity: o.quantity,
            expiredDate: o.expiredDate,
            activeTax: o.activeTax,
            promotionId: o.promotionId,
            requiredMember: o.requiredMember,
            discountSetting: o.discount
              ? {
                discountType: DISCOUNT_FORM_TYPE.DOLLAR,
                discountValue: o.discount,
              }
              : null,
            uuid: uuid(),
          })),
        };
        form.setFieldsValue(values);
        form.setFieldValue('initialItems', generateKeyInitialValue(values));
        delay(() => calculateCashier(), 200);
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      setPageLoading(false);
      delay(() => cashierCtrl.syncDraftBill(), 100);
    }
  };
  const switchTicketWithDetailData: ICashierControllerRef['switchTicketWithDetailData'] = async (info) => {
    const formValue: IUICashierFormValues = form.getFieldsValue();
    const values: IUICashierFormValues = {
      ...formValue,
      billId: info.billId,
      customerId: info.customerId || '',
      customerName: info.customerName || '',
      customerPhone: info.customerPhone || '',
      customerPoint: info.customerPoint || 0,
      ticketNumber: info.ticketNumber,
      createdDate: info.createdDated,
      saleTax: info.saleTax,
      useTax: info.useTax,
    };
    form.setFieldsValue(values);
    form.setFieldValue('initialItems', generateKeyInitialValue(values));
    delay(() => {
      calculateCashier();
    }, 200);
  };

  const saveTicketForPayment = async () => {
    try {
      await form.validateFields();
    } catch (error) {
      alertEmptyRef.current?.open();
      return '';
    }
    try {
      const values: IUICashierFormValues = form.getFieldsValue();
      const body: IApiUpdateTicketBody = mappingSavePayload(values);
      if (!body.items.length || (body.items.length === 1 && !body.items[0].quantity)) {
        alertEmptyRef.current?.open();
        return '';
      }
      const res: IResponseData<string> = await cashierApis.updateTicket(body);
      if (res.data.data) return res.data.data;
      throw res.data.data;
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg ?? '');
      return '';
    }
  };

  const saveTicketPaymentWithCallBack: ICashierControllerRef['saveTicketPaymentWithCallBack'] = async (callback) => {
    try {
      await form.validateFields();
    } catch (error) {
      alertEmptyRef.current?.open();
      return '';
    }
    checkStaffExist(async (staffId) => {
      setPageLoading(true);
      try {
        const values: IUICashierFormValues = form.getFieldsValue();
        const body: IApiUpdateTicketBody = mappingSavePayload({ ...values, staffId });
        if (!body.items.length || (body.items.length === 1 && !body.items[0].quantity)) {
          alertEmptyRef.current?.open();
          return '';
        }
        const res: IResponseData<string> = await cashierApis.updateTicket(body);
        if (res.data.data) return callback(res.data.data);
        throw res.data.data;
      } catch (error) {
        const msg = get(error, 'response.data.message');
        toast.error(msg ?? '');
        return callback('');
      } finally {
        setPageLoading(false);
      }
    });
  };

  const checkoutWithBillId: ICashierControllerRef['checkoutWithBillId'] = async (billId: string, staff: IStaffItem) => {
    form.setFieldValue('staffId', staff.id);
    dispatch(cashierService.actions.saveStaffCashier(staff));
    switchTicket(billId);
  };

  const checkoutWithDetailData: ICashierControllerRef['checkoutWithDetailData'] = async (info, staff: IStaffItem) => {
    dispatch(cashierService.actions.saveStaffCashier(staff));
    switchTicketWithDetailData({ ...info, staffId: staff.id });
  };

  const syncDraftBill = () => {
    const values: IUICashierFormValues = form.getFieldsValue();
    const date = (values.createdDate ? values.createdDate : moment().format('MM-DD-YYYY hh:mm A'));
    const draftData: UIPaymentDetailsData = {
      customerName: values.customerName || 'Walk-In',
      customerPhone: values.customerPhone,
      date,
      id: 'draft-ticket',
      orderNumber: (values.ticketNumber || 0).toString(),
      totalAmount: values.total,
      summary: {
        cardFee: 0,
        discount: values.discountByItem + values.discountByTicket,
        saleTax: values.saleTax,
        useTax: values.useTax,
        subtotal: values.subTotalBalance,
      },
      payments: {
        usedTax: values.useTax,
        subTotal: values.subTotalBalance,
        cardFee: 0,
        discount: values.discountByItem + values.discountByTicket,
        saleTax: values.saleTax,
        totalValue: values.total,
      },
      items: values.items.map((o) => ({
        id: o.itemId,
        discount: calcDiscountForItem(o),
        name: o.itemName,
        price: o.price,
        quantity: o.quantity,
        requiredMember: o.requiredMember,
        activeTax: o.activeTax,
      })),
    };
    socket.send(PAYMENT_PASS_DATA_TOPIC, {
      billId: 'draft-ticket',
      actor: TypePaymentActors.CASHIER,
      action: TypePaymentPassData.SEND_DRAFT_DATA,
      data: draftData,
    });
  };

  const chooseCustomer = (data: ISearchCustomerItem | null) => {
    const customerFields = {
      customerId: data?.id || '',
      customerName: data?.name || '',
      customerPhone: data ? formatPhone(data?.phone || '') : '',
      customerPoint: data?.point || 0,
      membership: data?.membership || null,
    };
    form.setFieldsValue(customerFields);
    const items: IUISelectItem[] = form.getFieldValue('items');
    const newItems = items.map(o => {
      return ({
        ...o, customerId: data?.id || '',
      });
    });
    form.setFieldValue('items', newItems);
    delay(() => {
      calculateCashier();
      cashierCtrl.syncDraftBill();
    }, 100);
  };

  const saveItemSelected = (item: IUISelectItem) => {
    const items: IUISelectItem[] = form.getFieldValue('items');
    const exist = items?.find((s) => s.itemId === item.itemId);
    if (!exist) {
      const newItems = [...items];
      newItems.push(item);
      form.setFieldValue('items', newItems);
      dispatch(cashierService.actions.setActiveItemLatest(item?.uuid || ''));
      delay(() => {
        if (bilSelectionRef.current) {
          bilSelectionRef.current.scrollTop = bilSelectionRef.current.scrollHeight;
        }
      }, 100);
      return;
    }
    dispatch(cashierService.actions.setActiveItemLatest(exist?.uuid || ''));
    form.setFieldValue('items', items?.map((s) => {
      if (s.itemId === exist.itemId) {
        return {
          ...s,
          quantity: exist.quantity + 1,
        };
      }
      return s;
    }) || []);
  };

  const chooseProduct: ICashierControllerRef['chooseProduct'] = (product) => {
    const customerId = form.getFieldValue('customerId');
    const item: IUISelectItem = {
      itemId: product.id,
      itemName: product.productName,
      discount: product.discount,
      itemType: CASHIER_ITEM_TYPE.PRODUCT,
      price: product.sellPrice,
      quantity: 1,
      uuid: uuid(),
      customerId: customerId || '',
      activeTax: product.activeTax,
      requiredMember: product.requiredMember,
      promotionId: product.promotion?.id || null,
    };
    if (product.discount)
      set(item, 'discountSetting', {
        discountType: DISCOUNT_FORM_TYPE.DOLLAR,
        discountValue: product.discount,
      });
    saveItemSelected(item);
  };

  const addQuickPayItem: ICashierControllerRef['addQuickPayItem'] = (quickpay) => {
    const item = {
      discount: 0,
      quantity: 1,
      itemId: 'QUICK_PAY_ID',
      itemName: quickpay.serviceName,
      itemType: CASHIER_ITEM_TYPE.PRODUCT,
      price: quickpay.amount || 0,
      uuid: uuid(),
      activeTax: true,
      requiredMember: false,
      promotionId: null,
    };
    saveItemSelected(item);
  };

  const addWeightItem: ICashierControllerRef['addWeightItem'] = (weight) => {
    const item = ({
      discount: 0,
      quantity: 1,
      itemId: 'QUICK_PAY_ID',
      itemName: weight.productName,
      itemType: CASHIER_ITEM_TYPE.PRODUCT,
      price: (weight.pricePerPound || 0) * parseFloat(weight.pound || ''),
      uuid: uuid(),
      activeTax: true,
      requiredMember: false,
      promotionId: null,
    });
    saveItemSelected(item);
  };

  const saleGiftCard = (values: IssueFormValues) => {
    const item = {
      discount: 0,
      quantity: 1,
      itemId: values.giftNumber,
      itemName: 'Gift Card',
      itemType: CASHIER_ITEM_TYPE.GIFT_CARD,
      price: values.amount || 0,
      expiredDate: moment(values.expiration).format('MM-DD-YYYY'),
      uuid: uuid(),
      requiredMember: false,
      activeTax: true,
      promotionId: '',
    };
    saveItemSelected(item);
  };

  const saleVIP = (o: IProductVIPItemData) => {
    const item = ({
      discount: 0,
      quantity: 1,
      itemId: o.id,
      activeTax: true,
      itemName: `VIP ${o.vipName}`,
      itemType: 'VIP',
      price: o.amount || 0,
      uuid: uuid(),
      requiredMember: false,
      promotionId: null,
    });
    saveItemSelected(item);
  };

  useImperativeHandle(ref, () => ({
    requestStaffForNewTicket,
    checkStaffExist,
    switchTicket,
    switchTicketWithDetailData,
    autoSaveTicket: autoSaveControl.call,
    saveTicketForPayment,
    saveTicketPaymentWithCallBack,
    checkoutWithBillId,
    checkoutWithDetailData,
    generateNewTicket,
    generateNewTicketAfterPayment,
    calculateCashier,
    syncDraftBill,
    chooseCustomer,
    chooseProduct,
    addQuickPayItem,
    addWeightItem,
    saleGiftCard,
    saleVIP,
  }));

  return (
    <FormWrapper form={form}
      ref={globalCashierDOMRef as any}
      onFinish={handleSaveTicket as any}
      onValuesChange={calculateCashier}
      initialValues={{ items: [] }}
      id='cashierForm'>
      <ContainerForm>
        <Form.Item name="initialItems" noStyle />
        <Form.Item name="billId" noStyle />
        <Form.Item name="createdDate" noStyle />
        <Form.Item name="discount" noStyle />
        <Form.Item name="subTotal" noStyle />
        <Form.Item name="saleTax" noStyle />
        <Form.Item name="useTax" noStyle />
        <Form.Item name="ticketNumber" noStyle />
        <Form.Item name="total" noStyle />
        <Form.Item name="feeCreditCard" noStyle />
        <Form.Item name="settingTax" noStyle />
        <Form.Item name="feeCreditCardAmount" noStyle />
        <Form.Item name="cashIncentive" noStyle />
        <Form.Item name="feeCashIncentiveAmount" noStyle />
        <Form.Item name="cashIncentiveAmount" noStyle />
        <Form.Item name="discountSetting" noStyle />
        <Form.Item name="customerPhone" noStyle />
        <Form.Item name="customerPoint" noStyle />
        <Form.Item name="customerName" noStyle />
        <Form.Item name="customerId" noStyle />
        <Form.Item name="discountTotal" noStyle />
        <Form.Item name="discountByItem" noStyle />
        <Form.Item name="discountByTicket" noStyle />
        <Form.Item name="membership" noStyle />
        <Form.Item name="items" noStyle rules={[{ required: true }]} />
        <Body>
          <CashierProducts />
          <CashierTicketInfo />
        </Body>
        <FooterActions />
        <ModalUpdateQuantity ref={globalModalUpdateQuantityRef} />
        <ModalEditPriceItem ref={globalModalEditPriceItemRef} />
        <DiscountByItem ref={globalDiscountByItemRef} />
        <ModalAlertEmpty ref={alertEmptyRef} />
        {modalAutoSaveContext}
      </ContainerForm>
    </FormWrapper>
  );
});
CashierPageUI.displayName = 'CashierPageUI';

const CashierPage = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    cashierScannerCtrl.current?.setForceStop(false);
    dispatch(cashierService.actions.init());
    return () => {
      cashierScannerCtrl.current?.setForceStop(true);
    };
  }, []);
  return <CashierPageUI ref={cashierRef} />;
};

export default CashierPage;


const FormWrapper = styled(Form)`
        display: flex;
        flex: 1;
        flex-direction: column;
        `;
const ContainerForm = styled.div`
        display: flex;
        flex: 1;
        flex-direction: column;
        `;

const Body = styled.div`
        flex: 1;
        display: grid;
        grid-template-columns: 3fr 1.5fr;
        `;
